import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useParcels } from "@/hooks/useParcels";
import { Parcel } from "@/mocks/parcels";
import ParcelInfoCard from "./components/ParcelInfoCard";
import LocationMap from "./components/LocationMap";
import StatusBadge from "./components/StatusBadge";
import ShipmentProgress from "./components/ShipmentProgress";

const TRACKING_FORMAT = /^SE-\d{9}$/;

export default function TrackingPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { getParcel } = useParcels();
  const navigate = useNavigate();

  const rawId = id ? decodeURIComponent(id) : "";
  const isValidFormat = TRACKING_FORMAT.test(rawId.toUpperCase());

  const [parcel, setParcel] = useState<Parcel | null>(null);
  const [fetching, setFetching] = useState(isValidFormat);
  const [notFound, setNotFound] = useState(false);

  // Redirect to home on refresh/direct URL access — only allow if navigated from within the app
  useEffect(() => {
    const navigated = sessionStorage.getItem("tracking_navigated");
    if (!navigated) {
      navigate("/", { replace: true });
      return;
    }
    // Clear the flag so a subsequent refresh also redirects home
    sessionStorage.removeItem("tracking_navigated");
  }, [navigate]);

  useEffect(() => {
    if (!isValidFormat) return;

    let cancelled = false;
    setFetching(true);
    setNotFound(false);
    setParcel(null);

    getParcel(rawId.toUpperCase()).then((result) => {
      if (cancelled) return;
      if (result) {
        setParcel(result);
      } else {
        setNotFound(true);
      }
      setFetching(false);
    });

    return () => { cancelled = true; };
  }, [rawId, isValidFormat, getParcel]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      {/* Loading state */}
      {isValidFormat && fetching && (
        <main className="flex-1 flex flex-col items-center justify-center py-24 text-center px-4 pt-28">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mb-6">
            <i className="ri-loader-4-line text-3xl text-emerald-500 animate-spin" />
          </div>
          <h2 className="text-lg font-semibold text-slate-700 mb-2">{t("tracking_looking_up")}</h2>
          <p className="text-slate-400 text-sm">
            {t("tracking_checking")}{" "}
            <span className="font-mono font-semibold">{rawId.toUpperCase()}</span>
          </p>
        </main>
      )}

      {/* Parcel found */}
      {parcel && (
        <>
          {/* Hero banner */}
          <div className="bg-gradient-to-br from-slate-800 via-slate-700 to-emerald-900 pt-28 pb-10 px-4 md:px-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-400/10 rounded-full blur-2xl pointer-events-none" />

            <div className="max-w-7xl mx-auto relative">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-emerald-400 transition-colors cursor-pointer mb-5"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line" />
                </div>
                {t("parcel_back_home")}
              </Link>

              <div className="mb-6">
                <p className="text-slate-400 text-sm font-medium mb-1">{t("tracking_number_label")}</p>
                <h1 className="text-2xl md:text-3xl font-bold text-white font-mono tracking-wide mb-3">
                  {parcel.id}
                </h1>
                <div className="flex flex-wrap items-center gap-3">
                  <StatusBadge status={parcel.status} size="md" />
                  {parcel.status !== "Delivered" && parcel.status !== "Exception" && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-300">
                      <span className="relative flex w-2 h-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full w-2 h-2 bg-emerald-400" />
                      </span>
                      {t("tracking_live_active")}
                    </span>
                  )}
                </div>
              </div>

              <ShipmentProgress parcel={parcel} />
            </div>
          </div>

          <main className="flex-1 py-8 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-2 flex flex-col gap-6">
                  <ParcelInfoCard parcel={parcel} />
                </div>
                <div className="lg:col-span-3 min-h-[520px]">
                  <LocationMap parcel={parcel} />
                </div>
              </div>
            </div>
          </main>
        </>
      )}

      {/* Invalid format */}
      {!isValidFormat && (
        <main className="flex-1 flex flex-col items-center justify-center py-24 text-center px-4 pt-28">
          <div className="w-24 h-24 flex items-center justify-center rounded-full bg-amber-100 border-4 border-amber-200 mb-6">
            <i className="ri-error-warning-line text-4xl text-amber-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">{t("tracking_invalid_format")}</h2>
          <p className="text-slate-500 text-sm max-w-sm mb-2">
            {t("tracking_invalid_desc")}{" "}
            <span className="font-semibold text-slate-700 font-mono bg-slate-100 px-2 py-0.5 rounded">&quot;{rawId}&quot;</span>
          </p>
          <p className="text-slate-400 text-sm max-w-sm mb-2">
            {t("tracking_invalid_format_hint")}{" "}
            <span className="font-mono font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">SE-000000000</span>{" "}
            (SE- followed by exactly 9 digits)
          </p>
          <p className="text-slate-400 text-xs max-w-sm mb-8">
            {t("tracking_invalid_copy_hint")}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </div>
            {t("tracking_try_again")}
          </Link>
        </main>
      )}

      {/* Not registered */}
      {isValidFormat && !fetching && notFound && (
        <main className="flex-1 flex flex-col items-center justify-center py-24 text-center px-4 pt-28">
          <div className="w-24 h-24 flex items-center justify-center rounded-full bg-red-100 border-4 border-red-200 mb-6">
            <i className="ri-search-eye-line text-4xl text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">{t("tracking_not_registered")}</h2>
          <p className="text-slate-500 text-sm max-w-sm mb-2">
            {t("tracking_not_registered_desc")}{" "}
            <span className="font-semibold text-slate-700 font-mono bg-slate-100 px-2 py-0.5 rounded">{rawId.toUpperCase()}</span>
          </p>
          <p className="text-slate-400 text-sm max-w-sm mb-8">
            {t("tracking_not_registered_hint")}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-home-line" />
            </div>
            {t("parcel_back_home")}
          </Link>
        </main>
      )}

      <Footer />
    </div>
  );
}
